import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './UserManagement.css';

function UserManagement() {
  const { isAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    name: '',
    role: 'user'
  });
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const [showTempPasswordModal, setShowTempPasswordModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  useEffect(() => {
    if (isAdmin()) {
      fetchUsers();
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/auth/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const validatePasswordStrength = (password) => {
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (name === 'password') {
      setPasswordStrength(validatePasswordStrength(value));
    }

    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!editingUser) {
      if (!formData.username.trim() || formData.username.length < 3) {
        newErrors.username = 'Username must be at least 3 characters';
      }
      if (!formData.email.trim() || !formData.email.includes('@')) {
        newErrors.email = 'Valid email is required';
      }

      // Validate password strength
      const strength = validatePasswordStrength(formData.password);
      const allRequirementsMet = Object.values(strength).every(req => req === true);

      if (!allRequirementsMet) {
        newErrors.password = 'Password must meet all requirements';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Show confirmation dialog before creating/updating user
    const action = editingUser ? 'update' : 'create';
    const confirmMessage = editingUser
      ? `Are you sure you want to update user "${formData.name}"?`
      : `Are you sure you want to create user "${formData.name}" with role "${formData.role}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      if (editingUser) {
        await api.put(`/auth/users/${editingUser._id}`, {
          name: formData.name,
          role: formData.role,
          isActive: formData.isActive
        });
        alert('User updated successfully');
        setShowForm(false);
        setEditingUser(null);
      } else {
        const response = await api.post('/auth/register', formData);
        // Show temporary password modal
        setTempPassword(response.data.temporaryPassword);
        setShowTempPasswordModal(true);
        setShowForm(false);
      }

      setFormData({
        username: '',
        email: '',
        password: '',
        name: '',
        role: 'user'
      });
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.errors
        ? error.response.data.errors.join('\n')
        : error.response?.data?.message || 'Failed to save user';
      alert(errorMessage);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
      isActive: user.isActive
    });
    setShowForm(true);
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt('Enter new password (min 6 characters):');

    if (!newPassword) return;

    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    try {
      await api.put(`/auth/users/${userId}/password`, {
        password: newPassword
      });
      alert('Password reset successfully');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password');
    }
  };

  const handleToggleActive = async (user) => {
    if (!window.confirm(`${user.isActive ? 'Disable' : 'Enable'} user ${user.name}?`)) {
      return;
    }

    try {
      await api.put(`/auth/users/${user._id}`, {
        isActive: !user.isActive
      });
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      alert('Failed to update user status');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/auth/users/${user._id}`);
      alert('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      name: '',
      role: 'user'
    });
    setErrors({});
  };

  if (!isAdmin()) {
    return (
      <div className="access-denied">
        <h2>Access Denied</h2>
        <p>You do not have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="user-management">
      <div className="page-header">
        <h2>User Management</h2>
        <button
          onClick={() => setShowForm(true)}
          className="btn btn-primary"
          disabled={showForm}
        >
          + Add User
        </button>
      </div>

      {showForm && (
        <div className="user-form-container">
          <form onSubmit={handleSubmit} className="form">
            <h3>{editingUser ? 'Edit User' : 'Create New User'}</h3>

            {!editingUser && (
              <>
                <div className="form-group">
                  <label htmlFor="username">Username *</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    className={errors.username ? 'error' : ''}
                    required
                  />
                  {errors.username && <span className="error-message">{errors.username}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                    required
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={errors.password ? 'error' : ''}
                    required
                  />
                  {errors.password && <span className="error-message">{errors.password}</span>}
                  {formData.password && (
                    <div className="password-requirements">
                      <small>Password Requirements:</small>
                      <ul>
                        <li className={passwordStrength.length ? 'met' : ''}>
                          {passwordStrength.length ? '✓' : '○'} At least 8 characters
                        </li>
                        <li className={passwordStrength.uppercase ? 'met' : ''}>
                          {passwordStrength.uppercase ? '✓' : '○'} At least one uppercase letter
                        </li>
                        <li className={passwordStrength.lowercase ? 'met' : ''}>
                          {passwordStrength.lowercase ? '✓' : '○'} At least one lowercase letter
                        </li>
                        <li className={passwordStrength.number ? 'met' : ''}>
                          {passwordStrength.number ? '✓' : '○'} At least one number
                        </li>
                        <li className={passwordStrength.special ? 'met' : ''}>
                          {passwordStrength.special ? '✓' : '○'} At least one special character
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
                required
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="role">Role *</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {editingUser && (
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  {' '}Active Account
                </label>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingUser ? 'Update User' : 'Create User'}
              </button>
              <button type="button" onClick={handleCancel} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Username</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>Last Login</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user._id} className={!user.isActive ? 'inactive-user' : ''}>
              <td>{user.name}</td>
              <td>{user.username}</td>
              <td>{user.email}</td>
              <td>
                <span className={`badge badge-${user.role}`}>
                  {user.role}
                </span>
              </td>
              <td>
                <span className={`badge ${user.isActive ? 'badge-active' : 'badge-inactive'}`}>
                  {user.isActive ? 'Active' : 'Disabled'}
                </span>
              </td>
              <td>
                {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
              </td>
              <td className="actions">
                <button
                  onClick={() => handleEdit(user)}
                  className="btn btn-small btn-secondary"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleResetPassword(user._id)}
                  className="btn btn-small btn-secondary"
                >
                  Reset Password
                </button>
                {/* Only show Disable/Enable button if not current user */}
                {user._id !== currentUser?.id && (
                  <button
                    onClick={() => handleToggleActive(user)}
                    className={`btn btn-small ${user.isActive ? 'btn-warning' : 'btn-success'}`}
                  >
                    {user.isActive ? 'Disable' : 'Enable'}
                  </button>
                )}
                {/* Only show Delete button if not current user */}
                {user._id !== currentUser?.id && (
                  <button
                    onClick={() => handleDelete(user)}
                    className="btn btn-small btn-danger"
                  >
                    Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Temporary Password Modal */}
      {showTempPasswordModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>User Created Successfully!</h2>
            <p className="modal-message">
              The user has been created. Please save the temporary password below and share it securely with the user.
            </p>
            <div className="temp-password-box">
              <label>Temporary Password:</label>
              <div className="password-display">
                <code>{tempPassword}</code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(tempPassword);
                    alert('Password copied to clipboard!');
                  }}
                  className="btn btn-small btn-secondary"
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="modal-warning">
              <strong>Important:</strong> The user will be required to change this password on their first login.
              Make sure to share this password securely (e.g., via encrypted email or in person).
            </div>
            <button
              onClick={() => setShowTempPasswordModal(false)}
              className="btn btn-primary btn-block"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
